import type { KeyboardEvent } from 'react';
import { useEffect, useId, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@eombor/ui';
import { setLocale, t, type Locale } from './index.js';
import { useLocale } from './useLocale.js';

interface LocaleOption {
  code: Locale;
  /** Tugmadagi qisqa kod — bayroq yonida, tarjima qilinmaydi. */
  short: string;
  /** Bayroq fayli: dumaloq, 512×512. */
  flag: string;
  /** Tilning O'Z nomidagi kaliti: ro'yxatni o'qiyotgan odam joriy tilni bilmasa ham topadi. */
  labelKey: string;
}

const OPTIONS: readonly LocaleOption[] = [
  { code: 'uz', short: 'UZ', flag: '/assets/flags/UZ.svg', labelKey: 'locale.uz' },
  { code: 'ru', short: 'RU', flag: '/assets/flags/RU.svg', labelKey: 'locale.ru' },
  { code: 'en', short: 'EN', flag: '/assets/flags/EN.svg', labelKey: 'locale.en' },
];

export interface LocalePickerProps {
  className?: string;
}

/**
 * Til tanlagich.
 *
 * Native `<select>` ISHLATILMAYDI: `<option>` ichiga rasm qo'yib bo'lmaydi
 * (brauzer cheklovi), bayroqsiz ro'yxat esa foydalanuvchi so'ragan narsa emas.
 * Shuning uchun bu yerda `role="listbox"` naqshi qo'lda yozilgan va u
 * klaviatura bilan to'liq ishlaydi: `↑`/`↓` yuradi, `Enter`/`Space` tanlaydi,
 * `Esc` yopadi va fokusni tugmaga qaytaradi.
 *
 * Tanlov `setLocale` ga tushadi — daraxtni qayta chizishni `LocaleProvider`
 * o'z zimmasiga oladi, bu komponent buni bilmaydi.
 */
export const LocalePicker = ({ className }: LocalePickerProps) => {
  const locale = useLocale();
  const listId = useId();
  const optionId = (index: number): string => `${listId}-option-${String(index)}`;

  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const currentIndex = Math.max(
    0,
    OPTIONS.findIndex((option) => option.code === locale),
  );
  const current = OPTIONS[currentIndex] ?? OPTIONS[0];

  /* Ochilganda tanlov joriy tildan boshlanadi va fokus ro'yxatga o'tadi —
     o'q tugmasi darhol ishlaydi, avval `Tab` bosish kerak emas. */
  useEffect(() => {
    if (!open) return;
    setActive(currentIndex);
    listRef.current?.focus();
  }, [open, currentIndex]);

  /* Tashqariga bosilsa yopiladi. `pointerdown` — `click` emas: ro'yxat
     boshqa tugma bosilganda ham darhol yo'qolsin. */
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent): void => {
      const target = event.target;
      if (target instanceof Node && rootRef.current?.contains(target) === true) return;
      setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  const close = (returnFocus: boolean): void => {
    setOpen(false);
    if (returnFocus) buttonRef.current?.focus();
  };

  const choose = (index: number): void => {
    const option = OPTIONS[index];
    if (option === undefined) return;
    // Joriy til bilan bir xil bo'lsa `setLocale` jim o'tadi — shart bu yerda
    // takrorlanmaydi, ro'yxat baribir yopiladi.
    setLocale(option.code);
    close(true);
  };

  const onListKeyDown = (event: KeyboardEvent<HTMLUListElement>): void => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((index) => (index + 1) % OPTIONS.length);
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((index) => (index - 1 + OPTIONS.length) % OPTIONS.length);
      return;
    }
    if (event.key === 'Home') {
      event.preventDefault();
      setActive(0);
      return;
    }
    if (event.key === 'End') {
      event.preventDefault();
      setActive(OPTIONS.length - 1);
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      choose(active);
      return;
    }
    if (event.key === 'Escape' || event.key === 'Tab') {
      event.preventDefault();
      close(true);
    }
  };

  const onButtonKeyDown = (event: KeyboardEvent<HTMLButtonElement>): void => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setOpen(true);
    }
  };

  return (
    <div ref={rootRef} className={cn('relative', className)}>
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`${t('locale.label')}: ${t(current?.labelKey ?? 'locale.uz')}`}
        onClick={() => setOpen((value) => !value)}
        onKeyDown={onButtonKeyDown}
        className={cn(
          'flex h-[var(--size-control-sm)] items-center gap-2 px-2.5',
          'rounded-[var(--radius-pill)] border border-border bg-surface/70 backdrop-blur',
          'text-xs font-medium text-text',
          'transition-colors duration-[var(--motion-fast)] ease-[var(--ease-standard)]',
          'hover:bg-surface-2',
        )}
      >
        <img src={current?.flag} alt="" width={20} height={20} className="size-5 shrink-0" />
        {current?.short}
        <ChevronDown
          size={14}
          aria-hidden
          className={cn(
            'text-text-faint transition-transform duration-[var(--motion-fast)] ease-[var(--ease-standard)]',
            open && 'rotate-180',
          )}
        />
      </button>

      {open ? (
        <ul
          ref={listRef}
          id={listId}
          role="listbox"
          tabIndex={-1}
          aria-label={t('locale.label')}
          aria-activedescendant={optionId(active)}
          onKeyDown={onListKeyDown}
          className={cn(
            'motion-pop-in absolute top-[calc(100%+0.375rem)] right-0 z-[var(--z-dropdown)]',
            'min-w-[11rem] overflow-hidden p-1',
            'rounded-[var(--radius-md)] border border-border bg-surface shadow-[var(--shadow-2)]',
          )}
        >
          {OPTIONS.map((option, index) => (
            <li
              key={option.code}
              id={optionId(index)}
              role="option"
              aria-selected={option.code === locale}
              // `onPointerDown` emas, `onClick`: tashqariga bosishni kuzatuvchi
              // `pointerdown` bu qatorni o'zi ham qamrab olmasin.
              onClick={() => choose(index)}
              onMouseMove={() => setActive(index)}
              className={cn(
                'flex cursor-pointer items-center gap-2.5 px-2.5 py-2',
                'rounded-[var(--radius-sm)] text-sm text-text',
                index === active && 'bg-surface-2',
                option.code === locale && 'font-medium',
              )}
            >
              <img src={option.flag} alt="" width={20} height={20} className="size-5 shrink-0" />
              <span className="min-w-0 flex-1 truncate">{t(option.labelKey)}</span>
              <span className="tabular text-xs text-text-faint">{option.short}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};

